// Shared revision/clarification thread panel for redesign + refinement: one
// rendering component, phase differences passed in as config/labels.

import { useEffect, useRef } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { formatAbsolute, formatRelative, OverlayScrollArea } from '@odo/shared-ui';
import {
    useClarificationHistory,
    DEFAULT_KIND_META,
    type ClarificationHistoryConfig,
    type ConversationEntry,
    type ConversationKind
} from './useClarificationHistory';

interface ConversationEntryCardProps {
    entry: ConversationEntry;
    isLatest: boolean;
    labels: Record<ConversationKind, string>;
    numberSuffix: string;
}

function ConversationEntryCard({
    entry,
    isLatest,
    labels,
    numberSuffix
}: ConversationEntryCardProps) {
    const meta = DEFAULT_KIND_META[entry.kind];
    return (
        <div
            className={`rounded-lg border overflow-hidden ${
                isLatest ? 'border-yellow-200 bg-yellow-50' : 'border-gray-200 bg-gray-50'
            }`}
        >
            <div className="px-4 py-2 flex items-center justify-between gap-3 border-b border-gray-100 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.badgeBg} ${meta.badgeText} flex-shrink-0`}
                    >
                        {entry.label ?? labels[entry.kind]}
                        {numberSuffix}
                    </span>
                    {isLatest && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex-shrink-0">
                            · Latest
                        </span>
                    )}
                </div>
                <div className="text-[11px] text-gray-500 flex items-center gap-2 min-w-0">
                    <span className="truncate">{entry.author}</span>
                    <span className="text-gray-300">·</span>
                    <span title={formatAbsolute(entry.createdAt)} className="whitespace-nowrap">
                        {formatRelative(entry.createdAt) || formatAbsolute(entry.createdAt)}
                    </span>
                </div>
            </div>
            <div className="px-4 py-3">
                <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {entry.text}
                </p>
            </div>
        </div>
    );
}

export interface ClarificationHistoryProps {
    patientId: string;
    config: ClarificationHistoryConfig;
    queryKeyPrefix: string;
    /** Overrides badge label text per kind; falls back to DEFAULT_KIND_META. */
    labelOverrides?: Partial<Record<ConversationKind, string>>;
    /** When set, appends " · #N" to that kind's badge, numbering occurrences in order. */
    numberKind?: ConversationKind;
    /** Render bare (no card chrome / title), for embedding under a tab header. */
    embedded?: boolean;
    /** Optional action rendered top-right, above the thread (e.g. "Request clarification"). */
    headerAction?: React.ReactNode;
}

// Merges the phase's revision request, resubmissions, and clarification
// ask/answer into one oldest-first, auto-scrolled thread.
export function ClarificationHistory({
    patientId,
    config,
    queryKeyPrefix,
    labelOverrides,
    numberKind,
    embedded = false,
    headerAction
}: ClarificationHistoryProps) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const { entries, isLoading, isError, isFetching, refetch } = useClarificationHistory(
        queryKeyPrefix,
        patientId,
        config
    );
    const labels: Record<ConversationKind, string> = {
        revision: labelOverrides?.revision ?? DEFAULT_KIND_META.revision.label,
        resubmission: labelOverrides?.resubmission ?? DEFAULT_KIND_META.resubmission.label,
        'clarification-requested':
            labelOverrides?.['clarification-requested'] ??
            DEFAULT_KIND_META['clarification-requested'].label,
        'clarification-provided':
            labelOverrides?.['clarification-provided'] ??
            DEFAULT_KIND_META['clarification-provided'].label
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [entries]);

    let numbered = 0;
    const body = (
        <OverlayScrollArea
            scrollRef={scrollRef}
            className="flex-1 min-h-0 flex flex-col"
            contentClassName="flex-1 px-5 py-4"
            innerClassName="min-h-full flex flex-col"
        >
            {isLoading ? (
                <div className="flex items-center justify-center flex-1 text-xs text-gray-400 italic">
                    Loading conversation…
                </div>
            ) : isError ? (
                <div className="flex items-center justify-center flex-1 text-xs text-red-500">
                    Failed to load conversation.
                </div>
            ) : entries.length === 0 ? (
                <div className="flex items-center justify-center flex-1 text-xs text-gray-400 italic text-center px-2">
                    No revision conversation yet.
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {entries.map((entry, i) => {
                        if (entry.kind === numberKind) numbered += 1;
                        const numberSuffix =
                            entry.kind === numberKind && numberKind ? ` · #${numbered}` : '';
                        return (
                            <ConversationEntryCard
                                key={entry.id}
                                entry={entry}
                                isLatest={i === entries.length - 1}
                                labels={labels}
                                numberSuffix={numberSuffix}
                            />
                        );
                    })}
                </div>
            )}
        </OverlayScrollArea>
    );

    if (embedded) {
        return (
            <div className="h-full flex flex-col min-h-0 font-inter">
                {headerAction && (
                    <div className="px-5 py-2.5 border-b border-gray-100 flex-shrink-0 flex justify-end">
                        {headerAction}
                    </div>
                )}
                {body}
            </div>
        );
    }

    return (
        <div className="flex-1 min-h-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden font-inter">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-bold text-sm text-gray-800 truncate">Clarifications</span>
                    {entries.length > 1 && (
                        <span className="text-[11px] text-gray-500 whitespace-nowrap">
                            ({entries.length})
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {headerAction}
                    <button
                        type="button"
                        onClick={refetch}
                        disabled={isFetching}
                        className="inline-flex items-center gap-1 px-2 py-[3px] rounded-md border border-gray-300 text-[11px] font-medium text-gray-500 bg-white hover:border-gray-400 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        aria-label="Refresh"
                    >
                        <ArrowPathIcon className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>
            {body}
        </div>
    );
}
